import { utcToZonedTime } from 'date-fns-tz';
import endOfQuarter from 'date-fns/endOfQuarter';
import isSameMonth from 'date-fns/isSameMonth';
import isSameYear from 'date-fns/isSameYear';
import isThisYear from 'date-fns/isThisYear';
import startOfQuarter from 'date-fns/startOfQuarter';
import { useIntl } from 'react-intl-next';
import { DIFF_YEAR_FORMAT, FULL_DATE_FORMAT, FULL_NICE_FORMAT, ISO_LOCAL_DATE, ISO_LOCAL_DATE_TIME, ISO_LOCAL_DATE_WITH_SLASHES, MONTH_ONLY_FORMAT, MONTH_ONLY_FORMAT_DIFF_YEAR, MONTH_ONLY_FORMAT_SHORT, MONTH_WITH_YEAR, NICE_DATE_TIME, SAME_MONTH_FORMAT, SAME_YEAR_FORMAT } from './date-constants';
import { differenceInCalendarWeeks, format, isSameWeek } from './date-fns-mixins';
import { tzDate } from './date-utils';
export var formatDateRange = function formatDateRange(from, to, locale) {
  var separator = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : '-';
  if (isSameYear(from, to)) {
    var fromDisplay = format(from, isSameMonth(from, to) ? SAME_MONTH_FORMAT : SAME_YEAR_FORMAT, {
      locale: locale
    });
    var toDisplay = formatNiceDate(to, locale);
    return "".concat(fromDisplay).concat(separator).concat(toDisplay);
  } else {
    var _fromDisplay = format(from, DIFF_YEAR_FORMAT, {
      locale: locale
    });
    var _toDisplay = format(to, DIFF_YEAR_FORMAT, {
      locale: locale
    });
    return "".concat(_fromDisplay).concat(separator).concat(_toDisplay);
  }
};
export var formatFullNiceDate = function formatFullNiceDate(date, locale, noDash) {
  return format(date, noDash ? DIFF_YEAR_FORMAT : FULL_NICE_FORMAT);
};
export var formatNiceMonthDate = function formatNiceMonthDate(date, locale) {
  return formatWithLocale(date, isThisYear(date) ? MONTH_ONLY_FORMAT : MONTH_ONLY_FORMAT_DIFF_YEAR, locale);
};
export var formatTooltipNiceMonthDate = function formatTooltipNiceMonthDate(date, locale) {
  return formatWithLocale(date, MONTH_WITH_YEAR, locale);
};
export var formatNiceQuarterDate = function formatNiceQuarterDate(date, locale) {
  return "".concat(formatWithLocale(startOfQuarter(date), MONTH_ONLY_FORMAT_SHORT, locale), "-").concat(formatWithLocale(endOfQuarter(date), isThisYear(date) ? MONTH_ONLY_FORMAT_SHORT : MONTH_ONLY_FORMAT_DIFF_YEAR, locale));
};
export var formatTooltipNiceQuarterDate = function formatTooltipNiceQuarterDate(date, locale) {
  return "".concat(formatWithLocale(startOfQuarter(date), MONTH_ONLY_FORMAT, locale), "-").concat(formatWithLocale(endOfQuarter(date), MONTH_WITH_YEAR, locale));
};
export var formatNiceDateString = function formatNiceDateString(dateString, locale) {
  return formatNiceDate(tzDate(dateString), locale);
};
export var formatNiceDate = function formatNiceDate(date, locale) {
  return formatWithLocale(date, isThisYear(date) ? SAME_YEAR_FORMAT : DIFF_YEAR_FORMAT, locale);
};
export var formatNiceDateTooltip = function formatNiceDateTooltip(date, locale) {
  return formatWithLocale(date, FULL_DATE_FORMAT, locale);
};
export var formatWithLocale = function formatWithLocale(date, dateFormat, locale) {
  return format(date, dateFormat, {
    locale: locale
  });
};
export var formatNiceDateTime = function formatNiceDateTime(date, locale) {
  return formatWithLocale(date, NICE_DATE_TIME, locale);
};
export var formatIsoLocateDateWithSlashes = function formatIsoLocateDateWithSlashes(date, locale) {
  return format(date, ISO_LOCAL_DATE_WITH_SLASHES, {
    locale: locale
  });
};
export var formatMonthOnlyDate = function formatMonthOnlyDate(date, locale) {
  return formatWithLocale(date, MONTH_ONLY_FORMAT, locale);
};

/**
 * TODO: Both of the methods below are used in constructions outside of React components.
 * They will need special consideration when we want to enforce the `locale` parameter.
 */

export var formatIsoLocalDate = function formatIsoLocalDate(date, locale) {
  return format(date, ISO_LOCAL_DATE, {
    locale: locale
  });
};
export var formatIsoLocalDateTime = function formatIsoLocalDateTime(date, locale) {
  return format(utcToZonedTime(date, 'UTC', {
    locale: locale
  }), ISO_LOCAL_DATE_TIME, {
    locale: locale
  });
};

/**
 * Format display text for a given interval using a week-window granularity against an "anchor" date
 * @param intl
 * @param start of interval
 * @param end of interval
 * @param anchorDate used as relative date, at which calculation will be performed against. If in doubt, leave empty
 * @param locale
 */
export var formatWeekRange = function formatWeekRange(intl, start, end) {
  var anchorDate = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : tzDate();
  var locale = arguments.length > 4 ? arguments[4] : undefined;
  if (isSameWeek(anchorDate, start, {
    locale: locale
  })) {
    return intl.formatMessage({
      id: 'townsquare.date-utils.week-range.this-week',
      description: 'This week section title',
      defaultMessage: 'This week'
    });
  } else if (isLastWeekOf(anchorDate, end, locale)) {
    return intl.formatMessage({
      id: 'townsquare.date-utils.week-range.last-week',
      description: 'Last week section title',
      defaultMessage: 'Last week'
    });
  } else {
    return formatDateRange(start, end, locale);
  }
};

/**
 * Format display text for a given interval using a week-window granularity against an "anchor" date
 * @param start of interval
 * @param end of interval
 * @param anchorDate used as relative date, at which calculation will be performed against. If in doubt, leave empty
 * @param locale provided as a param to avoid a circular dependency
 */
export var useFormatWeekRange = function useFormatWeekRange(start, end) {
  var anchorDate = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : tzDate();
  var locale = arguments.length > 3 ? arguments[3] : undefined;
  var intl = useIntl();
  return formatWeekRange(intl, start, end, anchorDate, locale);
};

/**
 * Is the older date last week of the newer date?
 */
export function isLastWeekOf(newer, older, locale) {
  return differenceInCalendarWeeks(newer, older, {
    locale: locale
  }) === 1;
}